import { createBrowserRouter, Navigate, useRouteError } from "react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  FullScreenLoader,
  RedirectIfAuthed,
  RequireAuth,
  RequireOnboarded,
  RequirePlatformAdmin,
} from "./auth/guards";

function RootErrorBoundary() {
  const { t } = useTranslation("common");
  const error = useRouteError();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="text-lg font-semibold">{t("ERROR_OCCURRED")}</h1>
      <p className="max-w-md text-sm text-muted-foreground break-words">
        {message}
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.reload()}
      >
        {t("RELOAD")}
      </Button>
    </div>
  );
}

/**
 * Every route module is loaded on demand and exports `Component`. Layouts
 * render an `<Outlet />`; guards are routes without a path.
 */
export const router = createBrowserRouter([
  {
    HydrateFallback: FullScreenLoader,
    ErrorBoundary: RootErrorBoundary,
    children: [
      { index: true, element: <Navigate to="/workspace" replace /> },
      { path: "home", element: <Navigate to="/sign-in" replace /> },
      // Campagnes n'est qu'un onglet du workspace : l'URL qu'on devine mène à
      // lui.
      {
        path: "campaigns",
        element: <Navigate to="/workspace?tab=campaigns-index" replace />,
      },
      {
        element: <RedirectIfAuthed />,
        children: [
          {
            lazy: () => import("./routes/auth/layout"),
            children: [
              { path: "sign-in", lazy: () => import("./routes/auth/sign-in") },
              { path: "sign-up", lazy: () => import("./routes/auth/sign-up") },
            ],
          },
        ],
      },
      { path: "auth/callback", lazy: () => import("./routes/auth/callback") },
      {
        element: <RequireAuth />,
        children: [
          {
            path: "onboarding",
            lazy: () => import("./routes/onboarding/layout"),
            children: [
              { index: true, lazy: () => import("./routes/onboarding/page") },
            ],
          },
          {
            element: <RequireOnboarded />,
            children: [
              {
                lazy: () => import("./routes/dashboard/layout"),
                children: [
                  {
                    path: "workspace",
                    lazy: () => import("./routes/dashboard/workspace"),
                  },
                  {
                    path: "settings",
                    lazy: () => import("./routes/dashboard/settings/layout"),
                    children: [
                      {
                        index: true,
                        element: <Navigate to="/settings/account" replace />,
                      },
                      {
                        path: "account",
                        lazy: () =>
                          import("./routes/dashboard/settings/account"),
                      },
                      {
                        path: "organization",
                        lazy: () =>
                          import("./routes/dashboard/settings/organization"),
                      },
                      {
                        path: "members",
                        lazy: () =>
                          import("./routes/dashboard/settings/members"),
                      },
                      {
                        path: "crm",
                        lazy: () => import("./routes/dashboard/settings/crm"),
                      },
                      {
                        path: "mcp",
                        lazy: () => import("./routes/dashboard/settings/mcp"),
                      },
                      {
                        path: "strategies",
                        lazy: () =>
                          import("./routes/dashboard/settings/strategies"),
                      },
                      {
                        path: "billing",
                        lazy: () =>
                          import("./routes/dashboard/settings/billing"),
                      },
                    ],
                  },
                  {
                    path: "admin",
                    element: <RequirePlatformAdmin />,
                    children: [
                      {
                        lazy: () => import("./routes/dashboard/admin/layout"),
                        children: [
                          {
                            index: true,
                            lazy: () => import("./routes/dashboard/admin/page"),
                          },
                          {
                            path: "scraper",
                            lazy: () =>
                              import("./routes/dashboard/admin/scraper/overview"),
                          },
                          {
                            path: "scraper/requests",
                            lazy: () =>
                              import("./routes/dashboard/admin/scraper/requests"),
                          },
                          {
                            path: "scraper/requests/:requestId",
                            lazy: () =>
                              import("./routes/dashboard/admin/scraper/request"),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: "*", lazy: () => import("./routes/not-found") },
    ],
  },
]);
